<script setup lang="ts">
import { computed, onMounted } from 'vue';
import {
  CdrLink, CdrText, IconCalendar, IconCaretRight, IconCreditCard, IconPrint,
} from '@rei/cedar';

import { useAnalytics } from '../../../../common/composables/useAnalytics';
import { useTags } from '../../../../common/composables/useTags';
import type { ReturnActionIcon, ReturnCardModel } from '../../../../api/types/ReturnCardModel';

const props = withDefaults(defineProps<{
  returnData: ReturnCardModel;
  pageName?: string;
}>(), {
  pageName: undefined,
});

// Four rather than five so the "+N" counter fits on the same line — a second
// line of thumbnails would push everything below it out of alignment
const MAX_THUMBNAILS = 4;

/** Cedar icons, by their kebab-case names: calendar, credit-card, print. */
const ACTION_ICONS = {
  calendar: IconCalendar,
  card: IconCreditCard,
  print: IconPrint,
};

const itemsLabel = computed(() => {
  const count = props.returnData.itemsReturningCount;
  return `${count} ${count === 1 ? 'item' : 'items'} returning`;
});

const thumbnails = computed(() => props.returnData.items.slice(0, MAX_THUMBNAILS));

const overflowCount = computed(() => Math.max(props.returnData.items.length - MAX_THUMBNAILS, 0));

const baseTag = useTags('getTag', 'HELP_CENTER_HELPCARD_RETURNS');

/**
* Analytics suffix per link, keyed by the id the model already carries.
*
* Kept apart from the ids on purpose: those also drive the `data-ui` selectors
* QA relies on, and the agreed tag format is the button label in PascalCase,
* which does not always match the id — `view_refund_details` renders as
* "View order details".
*/
const ACTION_TAGS: Record<string, string> = {
  refund_timing_estimate: 'RefundTimingEstimate',
  view_refund_details: 'ViewOrderDetails',
  view_return_label: 'ViewReturnStatus',
};

const HELP_LINK_TAGS: Record<string, string> = {
  'proof-of-purchase': 'ProofOfPurchaseNeeded',
  'original-packaging': 'OriginalPackagingRequired',
  'split-by-tender': 'HowRefundsAreSplitByTender',
};

const iconFor = (icon?: ReturnActionIcon) => (icon ? ACTION_ICONS[icon] : null);

const trackClick = (suffix?: string) => {
  if (!suffix) return;

  useAnalytics('sendClickAnalytics', {
    pageName: props.pageName,
    linkName: `${baseTag}_${suffix}`,
  });
};

/**
* View tag for this card, alongside the section-level one the carousel fires.
*
* Sent on render rather than on entering the viewport: a card can sit off
* screen inside the carousel, and what this measures is how often the card was
* served, not how far the customer scrolled.
*/
onMounted(() => {
  useAnalytics('sendViewAnalytics', {
    pageName: `${baseTag}_visible`,
  });
});
</script>

<template>
  <article
    class="help-card help-card--return return-card"
    data-ui="return-card"
  >
    <div class="help-card__body">
      <span class="help-card__chip help-card__chip--return">Return in progress</span>

      <CdrText
        tag="p"
        class="return-card__order"
      >
        Order #{{ returnData.orderNumber }}
      </CdrText>

      <CdrText
        tag="p"
        class="return-card__caption"
      >
        {{ itemsLabel }}
      </CdrText>

      <!-- Own row rather than beside the order line: a return can bring back
           several items, and inline they would crowd the text.
           Hidden from assistive tech because the item count above already
           says what these are -->
      <ul
        v-if="thumbnails.length"
        class="return-card__thumbnails"
        aria-hidden="true"
      >
        <li
          v-for="(item, index) in thumbnails"
          :key="`${returnData.key}-thumb-${index}`"
          class="return-card__thumbnail"
        >
          <img
            v-if="item.imageUrl"
            :src="item.imageUrl"
            alt=""
            class="return-card__image"
          >
          <span
            v-else
            class="return-card__image return-card__image--placeholder"
          />
        </li>
        <li
          v-if="overflowCount"
          class="return-card__overflow"
        >
          +{{ overflowCount }}
        </li>
      </ul>
    </div>

    <div
      v-if="returnData.actions.length"
      class="help-card__section"
    >
      <CdrText
        tag="h4"
        class="help-card__section-title"
      >
        Helpful actions
      </CdrText>
      <ul class="help-card__action-list">
        <li
          v-for="action in returnData.actions"
          :key="action.id"
          class="help-card__action"
        >
          <a
            :href="action.url"
            class="help-card__action-link"
            :data-ui="`return-card-action-${action.id}`"
            @click="trackClick(ACTION_TAGS[action.id])"
          >
            <component
              :is="iconFor(action.icon)"
              v-if="action.icon"
              class="help-card__action-icon"
              inherit-color
            />
            <span class="help-card__action-label">{{ action.label }}</span>
            <IconCaretRight
              class="help-card__action-caret"
              inherit-color
            />
          </a>
        </li>
      </ul>
    </div>

    <!-- --footer is what lines the cards up when the content above them is a
         different length -->
    <div
      v-if="returnData.relatedHelp.length"
      class="help-card__section help-card__section--footer"
    >
      <CdrText
        tag="h4"
        class="help-card__section-title"
      >
        Related help
      </CdrText>
      <ul class="return-card__help-list">
        <li
          v-for="link in returnData.relatedHelp"
          :key="link.id"
          class="return-card__help-item"
        >
          <CdrLink
            :href="link.url"
            :data-ui="`return-card-help-${link.id}`"
            @click="trackClick(HELP_LINK_TAGS[link.id])"
          >
            {{ link.label }}
          </CdrLink>
        </li>
      </ul>
    </div>
  </article>
</template>

<style lang="scss">
@import 'src/main/style/returnCards.scss';
</style>
